import React, { useState, useEffect } from "react";
import { Card, CardBody, CardHeader } from "../../../../_metronic/_partials/controls";
import { Button, TextField, MenuItem } from "@material-ui/core";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { getReviewById, updateReview, postReview } from "../../../../api/review";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";

const SOURCES = [
  { value: "trustpilot", label: "Trustpilot" },
  { value: "chrono24", label: "Chrono24" },
];

function getEmpty(sourceFromQuery) {
  const source =
    sourceFromQuery === "trustpilot" || sourceFromQuery === "chrono24"
      ? sourceFromQuery
      : "trustpilot";
  return {
    source,
    text: "",
    author: "",
    date: "",
    title: "",
  };
}

export default function EditReviewPage() {
  const location = useLocation();
  const qs = new URLSearchParams(location.search || "");
  const sourceFromQuery = qs.get("source") || "";

  const [review, setReview] = useState(getEmpty(sourceFromQuery));
  const { id } = useParams();
  const history = useHistory();
  const { isLoading: isLoadingData, disableLoading: disableLoadingData, ContentSkeleton } = useSkeleton();

  useEffect(() => {
    if (id) {
      getReviewById(id)
        .then((res) => {
          if (res.status === 200) {
            setReview({
              source: res.data.source || "trustpilot",
              text: res.data.text || "",
              author: res.data.author || "",
              date: res.data.date || "",
              title: res.data.title || "",
              order: res.data.order ?? 0,
            });
          }
          disableLoadingData();
        })
        .catch((err) => {
          alertError({ error: err, customMessage: "No se pudo cargar la review." });
          history.push("/reviews");
        });
    } else {
      setReview(getEmpty(sourceFromQuery));
      disableLoadingData();
    }
  }, [id, sourceFromQuery, disableLoadingData, history]);

  const handleChange = (field) => (e) => {
    setReview({ ...review, [field]: e.target.value });
  };

  const payload = () => {
    const p = {
      source: review.source,
      stars: 5,
      text: review.text || "",
      author: review.author || "",
      date: review.date || "",
      title: review.source === "chrono24" ? (review.title || "") : "",
    };
    if (id && typeof review.order === "number") p.order = review.order;
    return p;
  };

  const save = () => {
    if (!review.text?.trim() || !review.author?.trim()) {
      alertError({ customMessage: "Texto y autor son obligatorios." });
      return;
    }
    if (id) {
      updateReview(id, payload())
        .then((res) => {
          if (res.status === 200) {
            alertSuccess({ title: "Guardado", customMessage: "Review actualizada." });
            history.push("/reviews");
          }
        })
        .catch((err) => {
          alertError({ error: err, customMessage: "No se pudo actualizar." });
        });
    } else {
      postReview(payload())
        .then((res) => {
          if (res.status === 200 || res.status === 201) {
            alertSuccess({ title: "Creado", customMessage: "Review creada." });
            history.push("/reviews");
          }
        })
        .catch((err) => {
          alertError({ error: err, customMessage: "No se pudo crear." });
        });
    }
  };

  if (isLoadingData) return <ContentSkeleton />;

  return (
    <>
      <Card>
        <CardHeader title={id ? "Editar review" : "Nueva review"} />
        <CardBody>
          <TextField
            select
            label="Origen"
            value={review.source}
            onChange={handleChange("source")}
            variant="outlined"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          >
            {SOURCES.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Autor *"
            value={review.author}
            onChange={handleChange("author")}
            variant="outlined"
            fullWidth
            margin="normal"
            required
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Fecha (texto para mostrar, ej. 15 Jul 2024)"
            value={review.date}
            onChange={handleChange("date")}
            variant="outlined"
            fullWidth
            margin="normal"
            placeholder="15 Jul 2024"
            InputLabelProps={{ shrink: true }}
          />
          {review.source === "chrono24" && (
            <TextField
              label="Título (Chrono24)"
              value={review.title}
              onChange={handleChange("title")}
              variant="outlined"
              fullWidth
              margin="normal"
              placeholder="Rolex Submariner Date..."
              InputLabelProps={{ shrink: true }}
            />
          )}
          <TextField
            label="Texto *"
            value={review.text}
            onChange={handleChange("text")}
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            margin="normal"
            required
            InputLabelProps={{ shrink: true }}
          />
        </CardBody>
      </Card>
      <Button variant="outlined" onClick={() => history.push("/reviews")} style={{ marginRight: 16 }}>
        Volver
      </Button>
      <Button variant="contained" color="primary" onClick={save}>
        {id ? "Guardar" : "Crear"}
      </Button>
    </>
  );
}
